import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, Link } from 'react-router-dom';
import { loginUser, clearError } from '../store/slices/authSlice';
import { Mail, Lock, Eye, EyeOff, ArrowRight, AlertCircle, Activity, Zap, Sparkles, Moon } from 'lucide-react';
import '../index.css';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [formError, setFormError] = useState('');

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { loading, error } = useSelector((state) => state.auth);

  useEffect(() => {
    return () => {
      dispatch(clearError());
    };
  }, [dispatch]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError('');

    if (!email.trim() || !password.trim()) {
      setFormError('Please enter both email address and password.');
      return;
    }

    const result = await dispatch(loginUser({ email, password }));
    if (loginUser.fulfilled.match(result)) {
      navigate('/admin/dashboard', { replace: true });
    }
  };

  return (
    <div className="infra-login-wrapper">
      {/* LEFT HERO BANNER SECTION */}
      <section className="hero-section">
        <div className="hero-glow-effect"></div>
        <div className="hero-wave-overlay"></div>

        <div className="hero-content-layer">
          {/* Top Logo */}
          <div className="hero-logo">
            <div className="dots-grid-logo">
              {[...Array(9)].map((_, i) => (
                <div key={i} className="dot-red"></div>
              ))}
            </div>
            <div className="logo-text-container">
              <span className="logo-main-text">INFRAEDGE</span>
              <span className="logo-sub-text">RED LIGHT THERAPY</span>
            </div>
          </div>

          {/* Main Headline & Feature Bullets */}
          <div className="hero-main-body">
            <h1 className="hero-headline">
              Heal Smarter,<br />
              <span className="hero-headline-highlight">Live Better</span>
            </h1>
            <p className="hero-subheadline">
              Advanced Red Light Therapy solutions for a healthier, stronger you.
            </p>

            <div className="feature-list">
              <div className="feature-item">
                <div className="feature-icon-badge">
                  <Activity size={18} />
                </div>
                <div>
                  <h3 className="feature-title">Pain Relief</h3>
                  <p className="feature-desc">Reduces inflammation and soothes muscles & joint pain</p>
                </div>
              </div>

              <div className="feature-item">
                <div className="feature-icon-badge">
                  <Zap size={18} />
                </div>
                <div>
                  <h3 className="feature-title">Faster Recovery</h3>
                  <p className="feature-desc">Accelerates muscle repair and reduces recovery time</p>
                </div>
              </div>

              <div className="feature-item">
                <div className="feature-icon-badge">
                  <Sparkles size={18} />
                </div>
                <div>
                  <h3 className="feature-title">Better Skin</h3>
                  <p className="feature-desc">Boosts collagen production for healthier, glowing skin</p>
                </div>
              </div>

              <div className="feature-item">
                <div className="feature-icon-badge">
                  <Moon size={18} />
                </div>
                <div>
                  <h3 className="feature-title">Better Sleep & Mood</h3>
                  <p className="feature-desc">Supports deeper sleep and improves overall well-being</p>
                </div>
              </div>
            </div>
          </div>

          <div style={{ fontSize: '12px', color: '#64748b' }}>
            InfraEdge Technology Platform v1.0
          </div>
        </div>
      </section>

      {/* RIGHT FORM SECTION */}
      <section className="form-section">
        <div className="login-form-card">
          <div className="form-header">
            <h2 className="form-title">Welcome Back!</h2>
            <p className="form-subtitle">Sign in to access your InfraEdge Admin Account</p>
          </div>

          {(formError || error) && (
            <div className="form-alert">
              <AlertCircle size={18} />
              <span>{formError || error}</span>
            </div>
          )}

          <form onSubmit={handleSubmit} noValidate>
            <div className="input-group">
              <label className="input-label">Email Address</label>
              <div className="input-field-wrapper">
                <input
                  type="email"
                  className="styled-input"
                  placeholder="Info@infraedgeredlight.co.uk"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  disabled={loading}
                />
                <Mail className="field-icon" size={18} />
              </div>
            </div>

            <div className="input-group">
              <label className="input-label">Password</label>
              <div className="input-field-wrapper">
                <input
                  type={showPassword ? 'text' : 'password'}
                  className="styled-input"
                  placeholder="••••••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  disabled={loading}
                />
                <Lock className="field-icon" size={18} />
                <button
                  type="button"
                  className="password-toggle-btn"
                  onClick={() => setShowPassword(!showPassword)}
                  tabIndex="-1"
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            <div className="form-options-row">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  className="custom-checkbox"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                />
                <span>Remember me</span>
              </label>
              <Link to="/admin/forgot-password" className="forgot-pass-link">
                Forgot Password?
              </Link>
            </div>

            <button type="submit" className="btn-red-submit" disabled={loading}>
              {loading ? (
                <>
                  <div className="spinner-white"></div>
                  <span>Signing in...</span>
                </>
              ) : (
                <>
                  <span>Sign In</span>
                  <ArrowRight size={18} />
                </>
              )}
            </button>
          </form>

          <footer className="form-footer">
            © 2026 InfraEdge Red Light Therapy. All rights reserved.
          </footer>
        </div>
      </section>
    </div>
  );
};

export default Login;
